import axios from 'axios';
import { getVehicleDisplayName } from '../data/bookingConfig';
import { toDistanceMatrixPlace } from './routeMetrics';

const NOMINATIM_API = process.env.REACT_APP_NOMINATIM_API || 'https://nominatim.openstreetmap.org';
// const OSRM_API = process.env.REACT_APP_OSRM_API || 'https://router.project-osrm.org';
// const CORS_PROXY = 'https://cors-anywhere.herokuapp.com/';
const TELEGRAM_BOT_TOKEN = process.env.REACT_APP_TELEGRAM_BOT_TOKEN;
const TELEGRAM_CHAT_ID = process.env.REACT_APP_TELEGRAM_CHAT_ID;

// Nominatim API for location search
export const searchLocations = async (query) => {
  try {
    const response = await axios.get(`${NOMINATIM_API}/search`, {
      params: {
        q: query,
        format: 'json',
        limit: 8,
        addressdetails: 1,
        viewbox: '76.8,10.8,77.8,11.8', // Coimbatore area bounding box
        bounded: 0
      }
    });

    return response.data.map(result => result.display_name || `${result.name}, ${result.address_type}`);
  } catch (error) {
    console.error('Location search error:', error);
    return [];
  }
};

// Calculate actual route distance using Google Distance Matrix API
const getRouteDistance = async (pickup, dropoff) => {
  try {
    if (!window.google) {
      console.error('Google Maps not loaded');
      return null;
    }

    const service = new window.google.maps.DistanceMatrixService();
    
    const response = await service.getDistanceMatrix({
      origins: [toDistanceMatrixPlace(pickup)],
      destinations: [toDistanceMatrixPlace(dropoff)],
      travelMode: window.google.maps.TravelMode.DRIVING,
      unitSystem: window.google.maps.UnitSystem.METRIC
    });

    if (response.rows && response.rows[0] && response.rows[0].elements[0]) {
      const element = response.rows[0].elements[0];
      if (element.status === 'OK' && element.distance) {
        const distanceInKm = element.distance.value / 1000;
        return Math.round(distanceInKm * 10) / 10;
      }
    }
    return null;
  } catch (error) {
    console.error('Distance Matrix error:', error);
    return null;
  }
};

// OSRM API for distance calculation
export const calculateDistance = async (pickup, dropoff) => {
  try {
    console.log(`🔍 Calculating distance from ${pickup} to ${dropoff}`);
    
    // Try Google Distance Matrix API first (more accurate - uses actual routes)
    const routeDistance = await getRouteDistance(pickup, dropoff);
    if (routeDistance) {
      console.log(`✅ Route distance calculated: ${routeDistance} KM`);
      return routeDistance;
    }

    console.warn('⚠️ Distance Matrix API failed, trying fallback method');
    return null;
  } catch (error) {
    console.error('❌ Distance calculation error:', error.message);
    return null;
  }
};

// Generate WhatsApp message
export const generateWhatsAppMessage = (bookingData) => {
  return `Hello! I would like to book a taxi.

Trip Type: ${bookingData.tripType === 'oneway' ? 'One Way' : 'Round Trip'}
From: ${bookingData.pickupLocation}
To: ${bookingData.dropoffLocation}
Pickup Date: ${new Date(bookingData.pickupDate).toLocaleDateString()}
${bookingData.returnDate ? `Return Date: ${new Date(bookingData.returnDate).toLocaleDateString()}\n` : ''}
Vehicle: ${getVehicleDisplayName(bookingData.vehicle)}
Distance: ${bookingData.distance} km
Total Fare: ₹${bookingData.totalFare}

Passenger Details:
Name: ${bookingData.name}
Mobile: ${bookingData.mobile}
Adults: ${bookingData.adults}
Children: ${bookingData.children || 0}

Please confirm the booking.`;
};

// Send Telegram message
export const sendTelegramMessage = async (message) => {
  try {
    if (!TELEGRAM_BOT_TOKEN || !TELEGRAM_CHAT_ID) {
      console.error('❌ Telegram credentials not configured');
      console.log('TOKEN:', TELEGRAM_BOT_TOKEN ? 'Set' : 'Missing');
      console.log('CHAT_ID:', TELEGRAM_CHAT_ID ? 'Set' : 'Missing');
      return false;
    }

    console.log('📤 Sending to Telegram API...');
    const response = await axios.post(
      `https://api.telegram.org/bot${TELEGRAM_BOT_TOKEN}/sendMessage`,
      {
        chat_id: TELEGRAM_CHAT_ID,
        text: message,
        parse_mode: 'HTML'
      },
      {
        timeout: 5000 // 5 second timeout
      }
    );

    if (response.data && response.data.ok === true) {
      console.log('✅ Telegram message sent successfully');
      return true;
    } else {
      console.error('❌ Telegram API returned error:', response.data);
      return false;
    }
  } catch (error) {
    console.error('❌ Telegram error:', error.response?.data || error.message);
    if (error.response?.status === 401) {
      console.error('Authentication failed - check bot token');
    } else if (error.response?.status === 400) {
      console.error('Bad request - check chat ID or message format');
    }
    return false;
  }
};

// Send email through Netlify function
export const sendBookingEmail = async (bookingData) => {
  try {
    const response = await axios.post('/.netlify/functions/send-email', {
      to: bookingData.email,
      subject: 'Booking Confirmation - Easy Drop Taxi',
      data: bookingData
    });

    return response.data;
  } catch (error) {
    return null;
  }
};

const apiModule = {
  searchLocations,
  calculateDistance,
  generateWhatsAppMessage,
  sendTelegramMessage,
  sendBookingEmail
};

export default apiModule;
